import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { getRouteDistance } from 'helpers/routeDistance';

import Title from 'components/common/Layout/Title';
import FormRow, { ALIGN } from 'components/common/Layout/FormRow';
import CityAutoCompleteSelect from 'components/InputsField/formikInputs/CityAutoCompleteSelect';
import FormikTextField from 'components/InputsField/formikInputs/FormikTextField';
import FormikDateInput from 'components/InputsField/formikInputs/FormikDateField';

import { radiusInput } from 'components/InputsField/inputFormats';
import { ERROR_PLACE } from 'components/InputsField/InputError';

const ExchangeRoutes = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const isCalculating = useRef(false);

  // Calculate route distance when addresses change
  useEffect(() => {
    const calculateDistance = async () => {
      const loadingAddress = values.loading_address?.value || values.loading_address;
      const unloadingAddress = values.unloading_address?.value || values.unloading_address;

      if (!loadingAddress?.location || !unloadingAddress?.location) {
        return;
      }

      if (isCalculating.current) return;
      isCalculating.current = true;

      try {
        const distance = await getRouteDistance(loadingAddress, unloadingAddress);
        if (distance !== null) {
          setFieldValue('route_distance_meters', distance);
        }
      } catch (error) {
        console.error('Failed to calculate route distance:', error);
      } finally {
        isCalculating.current = false;
      }
    };

    calculateDistance();
  }, [values.loading_address, values.unloading_address, setFieldValue]);

  return (
    <>
      <Title withMargin>{t('transportFields.itinerary_section.heading')}</Title>
      <FormRow withSpace align={ALIGN.top}>
        <CityAutoCompleteSelect
          noOptionsMessage={t('forms.city.no_option')}
          name="loading_address"
          required
          label={t('transportFields.itinerary_section.uploading')}
          useMapboxSearch
          withMapPicker
        />
        <FormikTextField
          label={t('transportFields.itinerary_section.radius')}
          maxLength={3}
          name="loading_radius"
          placeholder="0"
          format={radiusInput}
          postfix={t('transportFields.itinerary_section.km')}
        />
      </FormRow>
      <FormRow withSpace withMargin align={ALIGN.top}>
        <CityAutoCompleteSelect
          noOptionsMessage={t('forms.city.no_option')}
          name="unloading_address"
          required
          label={t('transportFields.itinerary_section.unloading')}
          useMapboxSearch
          withMapPicker
        />
        <FormikTextField
          label={t('transportFields.itinerary_section.radius')}
          maxLength={3}
          name="unloading_radius"
          placeholder="0"
          format={radiusInput}
          postfix={t('transportFields.itinerary_section.km')}
        />
      </FormRow>
      <br />
      <FormRow withMargin>
        <FormRow withSpace withLine>
          <FormikDateInput
            name="available_from"
            label={t('transportFields.itinerary_section.vehicleAvailabilityTime')}
            minDate={new Date(new Date().setHours(0, 0, 0, 0))}
            errorMode={ERROR_PLACE.topRight}
            isRequired
          />
          <FormikDateInput
            name="available_to"
            minDate={values.available_from ? new Date(values.available_from) : new Date(Date.now())}
            errorMode={ERROR_PLACE.topRight}
          />
        </FormRow>
        <></>
      </FormRow>
    </>
  );
};

export default ExchangeRoutes;
